import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  useTheme,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Divider
} from '@mui/material';
import { 
  CalendarToday, 
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
  Event,
  SportsEsports
} from '@mui/icons-material';
import MKBox from './MKBox';
import MKTypography from './MKTypography';
import MKButton from './MKButton';
import { PRIMARY_PINK, PRIMARY_PINK_DARK } from '../constants';
import API_BASE_URL from '../apiConfig';
import MajorModal from './MajorModal';

function MajorDisplay({ clubId, clubName, isOwner = false, onMajorClick }) {
  const theme = useTheme();
  const [majors, setMajors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showMajorModal, setShowMajorModal] = useState(false);
  const [editingMajor, setEditingMajor] = useState(null);
  const [deletingMajor, setDeletingMajor] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  useEffect(() => {
    fetchMajors();
  }, [clubId]);

  const fetchMajors = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await fetch(`${API_BASE_URL}/api/majors/club/${clubId}`, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch majors');
      }

      const majorsData = await response.json();
      setMajors(majorsData);
    } catch (error) {
      console.error('Error fetching majors:', error);
      setError('Failed to load majors');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMajor = () => {
    setEditingMajor(null);
    setShowMajorModal(true);
  };

  const handleEditMajor = (major) => {
    setEditingMajor(major);
    setShowMajorModal(true);
  };

  const handleDeleteMajor = (major) => {
    setDeletingMajor(major);
    setShowDeleteDialog(true);
  };

  const confirmDeleteMajor = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/majors/${deletingMajor.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to delete major');
      }

      await fetchMajors(); // Refresh the list
      setShowDeleteDialog(false);
      setDeletingMajor(null);
    } catch (error) {
      console.error('Error deleting major:', error);
      setError('Failed to delete major');
    }
  };

  const handleMajorCreated = (major) => {
    fetchMajors(); // Refresh the list
  };

  const handleMajorClick = (major) => {
    if (onMajorClick) {
      onMajorClick(major);
    } else {
      // Default behavior - navigate to major page
      window.open(`/majors/${major.id}`, '_blank');
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "TBD";
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  if (loading) {
    return (
      <Box sx={{ 
        minHeight: "200px", 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center' 
      }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ minHeight: "200px", py: 4 }}>
        <Container maxWidth="lg">
          <Alert severity="error" sx={{ textAlign: 'center' }}>
            {error}
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", pt: 0, pb: 0 }}>
      <Container maxWidth="lg">
        {/* Header with Create Button */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 4 
        }}>
          <MKTypography
            variant="h3"
            fontWeight="bold"
            sx={{ 
              color: theme.palette.mode === 'dark' ? 'white !important' : '#333333 !important'
            }}
          >
            Major Events
          </MKTypography>
          
          {isOwner && (
            <MKButton
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateMajor}
              sx={{
                backgroundColor: PRIMARY_PINK,
                color: 'white !important',
                '&:hover': {
                  backgroundColor: PRIMARY_PINK_DARK,
                  color: 'white !important',
                },
              }}
            >
              Create Major
            </MKButton>
          )}
        </Box>

        {majors.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <SportsEsports sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
            <MKTypography
              variant="h5"
              sx={{ 
                color: theme.palette.mode === 'dark' ? 'white !important' : '#333333 !important',
                mb: 2
              }}
            >
              No Major Events Yet
            </MKTypography>
            <MKTypography
              variant="body1"
              sx={{ 
                color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7) !important' : '#666 !important',
                mb: 3
              }}
            >
              Create your first major event to group related tournaments and competitions.
            </MKTypography>
            {isOwner && (
              <MKButton
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateMajor}
                sx={{
                  backgroundColor: PRIMARY_PINK,
                  color: 'white !important',
                  '&:hover': {
                    backgroundColor: PRIMARY_PINK_DARK,
                    color: 'white !important',
                  },
                }}
              >
                Create Your First Major
              </MKButton>
            )}
          </Box>
        ) : (
          <Box>
            {majors.map((major, majorIndex) => (
              <Box key={major.id} sx={{ mb: 8 }}>
                {/* Major Banner */}
                {major.banner_url && (
                  <Box sx={{ textAlign: "center", mb: 4 }}>
                    <Card
                      sx={{
                        maxWidth: 800,
                        mx: "auto",
                        pb: 0,
                        overflow: "hidden",
                        borderRadius: 3,
                        boxShadow: theme.palette.mode === 'dark' 
                          ? '0 8px 32px rgba(0,0,0,0.3)' 
                          : '0 8px 32px rgba(0,0,0,0.1)'
                      }}
                    >
                      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                        <img
                          src={major.banner_url}
                          alt={`${major.title} banner`}
                          style={{
                            width: '100%',
                            height: 'auto',
                            display: 'block'
                          }}
                          loading="lazy"
                          onError={(e) => {
                            console.error('Failed to load banner image:', major.banner_url);
                            e.target.style.display = 'none';
                          }}
                          onLoad={() => {
                            console.log('Banner image loaded successfully:', major.banner_url);
                          }}
                        />
                      </CardContent>
                    </Card>
                  </Box>
                )}

                {/* Major Title */}
                {major.show_title && (
                  <MKTypography
                    variant="h3"
                    fontWeight="bold"
                    mb={4}
                    sx={{ 
                      textAlign: "center",
                      color: theme.palette.mode === 'dark' ? 'white !important' : '#333333 !important'
                    }}
                  >
                    {major.title}
                  </MKTypography>
                )}

                {/* Major Actions */}
                {isOwner && (
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    gap: 1, 
                    mb: 3 
                  }}>
                    <MKButton
                      variant="outlined"
                      startIcon={<EditIcon />}
                      onClick={() => handleEditMajor(major)}
                      sx={{ 
                        borderColor: PRIMARY_PINK, 
                        color: PRIMARY_PINK,
                        '&:hover': {
                          backgroundColor: PRIMARY_PINK,
                          color: 'white !important',
                          borderColor: PRIMARY_PINK,
                        }
                      }}
                    >
                      Edit
                    </MKButton>
                    <MKButton
                      variant="outlined"
                      startIcon={<VisibilityIcon />}
                      onClick={() => handleMajorClick(major)}
                      sx={{ 
                        borderColor: PRIMARY_PINK, 
                        color: PRIMARY_PINK,
                        '&:hover': {
                          backgroundColor: PRIMARY_PINK,
                          color: 'white !important',
                          borderColor: PRIMARY_PINK,
                        }
                      }}
                    >
                      View
                    </MKButton>
                    <MKButton
                      variant="outlined"
                      startIcon={<DeleteIcon />}
                      onClick={() => handleDeleteMajor(major)}
                      sx={{ 
                        borderColor: 'error.main', 
                        color: 'error.main',
                        '&:hover': {
                          backgroundColor: 'error.main',
                          color: 'white !important',
                          borderColor: 'error.main',
                        }
                      }}
                    >
                      Delete
                    </MKButton>
                  </Box>
                )}

                {/* Major Info */}
                <Box sx={{ mb: 4 }}>
                  <Grid container spacing={2} justifyContent="center">
                    <Grid item>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CalendarToday sx={{ mr: 1, color: PRIMARY_PINK }} />
                        <MKTypography variant="body2" color="text.secondary">
                          {formatDateTime(major.start_date)} - {formatDateTime(major.end_date)}
                        </MKTypography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>

                {/* Events Section */}
                {major.events && major.events.length > 0 && (
                  <Box sx={{ mb: 4 }}>
                    <MKTypography
                      variant="h4"
                      fontWeight="bold"
                      mb={2}
                      sx={{ 
                        textAlign: "center",
                        color: theme.palette.mode === 'dark' ? 'white !important' : '#333333 !important'
                      }}
                    >
                      {major.events_header}
                    </MKTypography>
                    
                    <Box sx={{ 
                      maxWidth: 600, 
                      mx: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1
                    }}>
                      {major.events.map((event, eventIndex) => (
                        <Card
                          key={event.id}
                          sx={{
                            border: `1px solid ${PRIMARY_PINK}`,
                            borderRadius: 2,
                            transition: 'all 0.2s ease',
                            cursor: 'pointer',
                            '&:hover': {
                              transform: 'translateX(4px)',
                              boxShadow: `0 4px 12px ${PRIMARY_PINK}40`
                            }
                          }}
                          onClick={() => window.open(`/events/${event.id}`, '_blank')}
                        >
                          <CardContent sx={{ p: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Box sx={{ 
                                minWidth: 40, 
                                height: 40, 
                                borderRadius: '50%', 
                                backgroundColor: PRIMARY_PINK,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontWeight: 'bold'
                              }}>
                                {eventIndex + 1}
                              </Box>
                              <Box sx={{ flex: 1 }}>
                                <MKTypography
                                  variant="body1"
                                  fontWeight="bold"
                                  sx={{ 
                                    color: theme.palette.mode === 'dark' ? 'white !important' : '#333333 !important'
                                  }}
                                >
                                  {event.title}
                                </MKTypography>
                                <MKTypography variant="body2" color="text.secondary">
                                  {formatDateTime(event.start_time)}
                                </MKTypography>
                              </Box>
                              <Event sx={{ color: PRIMARY_PINK }} />
                            </Box>
                          </CardContent>
                        </Card>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Additional Content */}
                {major.content && (
                  <Box sx={{ mb: 4 }}>
                    <Card
                      sx={{
                        maxWidth: 800,
                        mx: "auto",
                        background: theme.palette.mode === 'dark' 
                          ? 'linear-gradient(135deg, #2d2d2d 0%, #1a1a1a 100%)'
                          : 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                        border: theme.palette.mode === 'dark' 
                          ? '1px solid #444'
                          : '1px solid #dee2e6',
                        borderRadius: 3,
                        boxShadow: theme.palette.mode === 'dark' 
                          ? '0 8px 32px rgba(0,0,0,0.3)' 
                          : '0 8px 32px rgba(0,0,0,0.1)'
                      }}
                    >
                      <CardContent sx={{ p: 4 }}>
                        <MKTypography
                          variant="body1"
                          sx={{ 
                            color: theme.palette.mode === 'dark' ? 'white !important' : '#333333 !important',
                            whiteSpace: 'pre-line'
                          }}
                        >
                          {major.content}
                        </MKTypography>
                      </CardContent>
                    </Card>
                  </Box>
                )}

                {majorIndex < majors.length - 1 && <Divider sx={{ my: 4 }} />}
              </Box>
            ))}
          </Box>
        )}

        {/* Major Modal */}
        <MajorModal
          open={showMajorModal}
          onClose={() => {
            setShowMajorModal(false);
            setEditingMajor(null);
          }}
          onMajorCreated={handleMajorCreated}
          clubId={clubId}
          clubName={clubName}
          major={editingMajor}
        />

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={showDeleteDialog}
          onClose={() => setShowDeleteDialog(false)}
          PaperProps={{
            sx: {
              backgroundColor: theme.palette.mode === 'dark' ? '#1a1a1a' : '#ffffff',
              borderRadius: 3
            }
          }}
        >
          <DialogTitle sx={{ 
            backgroundColor: 'error.main', 
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Typography variant="h6" fontWeight="bold">
              Delete Major
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <Typography>
              Are you sure you want to delete "{deletingMajor?.title}"? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button 
              onClick={() => setShowDeleteDialog(false)}
              sx={{ color: 'text.primary' }}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmDeleteMajor}
              variant="contained"
              color="error"
              sx={{ color: 'white !important' }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
}

export default MajorDisplay;
